import { request } from './client'
import type { StaffAuth } from './teaching'
import type { ParentAuth } from './parent'

/**
 * The school notice board, from both ends.
 *
 * 🔴 THE PARENT ROUTE TAKES NO SCHOOL ID, AND THAT IS THE AUTHORISATION RATHER
 * THAN A CONVENIENCE. A parent's token carries no school — their scope is their
 * children — so the server derives which schools may speak to them from their
 * own children's enrolment. A path with a school id in it would be a path where
 * typing another school's id returns its notices, which is §8.1's rule stated as
 * a URL.
 *
 * ⚠️ POST, NEVER PATCH OR DELETE — `client.ts` types `method` as
 * `'GET' | 'POST'`. That is not the only reason there is no edit route: by the
 * time anyone could change the words, the outbox has carried them to a parent's
 * phone, and a screen showing different text from the message a family received
 * is worse than no screen. A correction is a second announcement.
 */

/** What a school reads back on its own board. */
export interface AnnouncementRow {
  id: string
  /** Null for a school-wide notice; a class id for one section's families. */
  classId: string | null
  title: string
  body: string
  /** The school's name, or the person who posted. Never a phone or an email. */
  authorLabel: string
  whatsapp: boolean
  /**
   * How many sends were queued.
   *
   * ⚠️ NOT HOW MANY ARRIVED. The outbox owns delivery and reports it per row;
   * zero with `whatsapp: true` is the honest shape of "we tried and every family
   * had opted out".
   */
  enqueued: number
  createdAt: string
}

/** What a parent reads. Carries the school's name; carries no send count. */
export interface ParentAnnouncement {
  id: string
  title: string
  body: string
  authorLabel: string
  /** Which school is speaking — a parent may have children at two. */
  schoolName: string
  /**
   * 🔴 FOR THEIR CHILD'S CLASS RATHER THAN FOR EVERY FAMILY. "The school is
   * closed" and "bring trainers on Friday" call for different actions, and the
   * second only makes sense once a parent knows which room it is about.
   */
  forClass: boolean
  createdAt: string
}

export interface PostAnnouncementInput {
  title: string
  body: string
  /**
   * 🔴 AN EXPLICIT DECISION, NEVER A DEFAULT. This is the difference between
   * posting to a board and messaging four hundred families' personal phones.
   */
  whatsapp: boolean
  /**
   * 🔴 WHO IT IS FOR, AND THEREFORE WHO MAY SEND IT. Omitted means the whole
   * school, which is ADMIN-only; a class id means that section's families, which
   * a teacher may do for her own sections. The server resolves the permission
   * from this field rather than from the caller's role — one route, and no way
   * to broadcast school-wide by forgetting a flag.
   */
  classId?: string
}

/** How many notices one page of the parent feed holds (the server's own page size). */
export const PARENT_FEED_PAGE = 50

/**
 * The parent's notices, newest first; with `before`, the page older than that
 * notice (P1-63). A full page means there may be an older one.
 */
export const getParentAnnouncements = (auth: ParentAuth = {}, before?: string): Promise<ParentAnnouncement[]> =>
  request(`/parent/announcements${before ? `?before=${encodeURIComponent(before)}` : ''}`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * ⚠️ `classId` NARROWS THE BOARD TO ONE SECTION — the teacher's own view. An
 * admin passes nothing and gets the whole school's, which is the board they are
 * responsible for. The server checks the id before it narrows anything.
 */
export const getSchoolAnnouncements = (
  auth: StaffAuth = {},
  classId?: string,
): Promise<AnnouncementRow[]> =>
  request(
    classId === undefined
      ? '/school/announcements'
      : `/school/announcements?classId=${encodeURIComponent(classId)}`,
    {
      context: 'user',
      token: auth.token,
      signal: auth.signal,
    },
  )

export const postAnnouncement = (
  input: PostAnnouncementInput,
  auth: StaffAuth = {},
): Promise<AnnouncementRow> =>
  request('/school/announcements', {
    method: 'POST',
    body: input,
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })
